import { Network } from '@/hooks/network/useNetwork'
import { BayesianAssumptionSet } from '@/services/bayesianApi/models'
import {
  OptimizationOptions,
  OptimizeRequestData,
} from '@/services/bayesianApi/optimizationModels'
import { Assumption } from '@/services/models/assumptionSet/assumptions'
import { AssumptionSet } from '@/services/models/assumptionSet/assumptionSets'
import { Requirement } from '@/services/models/assumptionSet/requirements'
import { getNonDefaultRequirements } from '@/utils/assumptionSets/checkIfRequirementIsDefault'
import {
  buildVirtualEvidence,
  transitionEvidenceVariableFor,
} from '@/utils/bayesian/assumptionEvidence'
import { createBayesianNodesForInfer } from '@/utils/bayesian/inferUtils'

export const createOptimizeRequestData = (
  network: Network | undefined,
  selectedVariables: Set<string>,
  assumptionSets: AssumptionSet[] | undefined,
  assumptions: Assumption[] | undefined,
  requirements: Requirement[] | undefined,
  options: OptimizationOptions,
  networkId: string,
  jobName: string,
): OptimizeRequestData => {
  if (!network || !assumptionSets || !assumptions || !requirements) {
    return {} as OptimizeRequestData
  }
  return {
    network: createBayesianNodesForInfer(network),
    selectedNodes: Array.from(selectedVariables),
    assumption_sets: assumptionSets
      // KBE-593: a silenced set must never constrain the optimizer, even if one
      // of its child requirements was individually un-silenced (the set-level
      // flag dominates). Requirement-level silencing is handled inside
      // createAssumptionSetForOptimizeRequest.
      .filter((assumptionSet) => !assumptionSet.isSilenced)
      .map((r) =>
        createAssumptionSetForOptimizeRequest(
          r,
          assumptions,
          requirements,
          network,
        ),
      )
      .filter((r) => Object.keys(r.requirements).length > 0),
    options: options,
    networkId: networkId,
    jobName: jobName,
  }
}
const createAssumptionSetForOptimizeRequest = (
  assumptionSet: AssumptionSet,
  allAssumptions: Assumption[],
  requirements: Requirement[],
  network: Network,
): BayesianAssumptionSet => {
  const assumptions = allAssumptions
    .filter((o) => o.assumptionSetId === assumptionSet.id)
    .map((o) => {
      // KBE-774: a researcher's numeric value becomes virtual evidence so the
      // optimizer conditions on the same distribution inference does, instead of
      // the state the value happened to snap to. `observedState` must go null
      // alongside it — the API rejects an assumption setting both. Nulling it is
      // safe for the parentless-node handling flagged below: that path keys off
      // `assumptions.length`, not the observed values, and this still emits one
      // entry per assumption.
      const virtualEvidence = buildVirtualEvidence({
        intervalValue: o.intervalValue,
        variable: transitionEvidenceVariableFor(
          network.variableById.get(o.variableId),
        ),
        network,
        // An optimization job runs for hours and writes its result back as CPTs, so
        // it refuses rather than conditioning on substituted values (KBE-839).
        refuseUntrustworthyData: true,
      })
      if (virtualEvidence) {
        return {
          variableId: o.variableId,
          observedState: null,
          virtualEvidence,
        }
      }
      return {
        variableId: o.variableId,
        // null is the optimize model's "unobserved" sentinel. Keep the entry (as
        // opposed to dropping it) so an all-unobserved set stays non-empty: the
        // parentless-node handling in getFixedNodesAndUpdatedCPTsIfNeeded treats
        // an empty assumptions list as an unconditional set, so omitting would
        // reclassify the set and overwrite CPTs incorrectly. stateIndex is never
        // -1 here — getAllAssumptions throws on a dangling reference.
        observedState: o.stateIndex ?? null,
      }
    })

  // KBE-593: silenced requirements must never constrain the optimizer. Both
  // optimize entry points (server `buildOptimizeRequestData` and the UI's
  // `useVariableOptimizationJob`) funnel through here, so this single exclusion
  // keeps silenced items out of every optimize payload.
  const assumptionSetsRequirements = requirements.filter(
    (requirement) =>
      requirement.assumptionSetId === assumptionSet.id &&
      !requirement.isSilenced,
  )
  const variablesWithRequirements = new Set(
    assumptionSetsRequirements.map((requirement) => requirement.variableId),
  )

  return {
    id: assumptionSet.id,
    name: assumptionSet.name,
    assumptions,
    requirements: createRequirementForNodes(
      variablesWithRequirements,
      assumptionSetsRequirements,
      network,
    ),
  }
}

const createRequirementForNodes = (
  variablesWithRequirements: Set<string>,
  assumptionSetsRequirements: Requirement[],
  network: Network,
): Record<
  string,
  Record<number, { expected_value: number; tolerance_value: number }>
> => {
  const requirements = Array.from(variablesWithRequirements)
    .map((variableId) => {
      const variable = network.variableById.get(variableId)
      if (!variable)
        throw new Error(
          'Variable should be defined (createRequirementForNodes)',
        )

      const nonDefaultRequirements = getNonDefaultRequirements(
        assumptionSetsRequirements,
      )
      const requirement = nonDefaultRequirements
        .filter((requirement) => requirement.variableId === variableId)
        .map((requirement) => ({
          state: variable.stateIds.indexOf(requirement.stateId),
          interval: {
            expected_value: requirement.expectedValue,
            tolerance_value: requirement.toleranceValue,
          },
        }))

      return {
        variableId,
        requirement,
      }
    })
    .filter((variable) => variable.requirement.length > 0)

  return Object.fromEntries(
    requirements.map((requirement) => [
      requirement.variableId,
      Object.fromEntries(
        requirement.requirement.map((interval) => [
          interval.state,
          interval.interval,
        ]),
      ),
    ]),
  )
}
